import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import {
  DOC_MAX_BLOCK_LENGTH,
  insertLink,
  needsRenumber,
  orderBetween,
  parseMarkdownLine,
  renumber,
  sortBlocks,
  toggleMark,
  type DocBlock,
  type DocBlockStyle,
  type DocInlineMark,
  type DocPresence,
} from '@itsappy/shared';
import { useSocket } from '@/realtime/socketContext';
import { useAuth } from '@/store/auth';
import { cn } from '@/lib/utils';
import { Spinner } from '@/components/ui';
import { DocToolbar } from './DocToolbar';

/**
 * A collaborative document.
 *
 * Each paragraph is its own textarea and its own addressable unit: edits are
 * sent per paragraph and merged per paragraph, so two people writing in
 * different parts of the same document both keep their work. Within one
 * paragraph the last write wins, which is the trade this design makes — it is
 * the boundary at which two people editing genuinely conflict.
 */

const newId = () =>
  // Stable across clients without a round trip; a collision would have to be
  // two people generating the same 12 hex characters in the same document.
  Array.from(crypto.getRandomValues(new Uint8Array(6)))
    .map((n) => n.toString(16).padStart(2, '0'))
    .join('');

export interface DocEditorProps {
  docId: string;
  blocks: DocBlock[];
  canEdit: boolean;
  onBlocksChanged?: (blocks: DocBlock[]) => void;
  className?: string;
}

/** How each paragraph style is drawn, and what it looks like while empty. */
const STYLE_CLASS: Record<DocBlockStyle, string> = {
  p: 'text-sm leading-7 text-ink',
  h1: 'text-2xl font-semibold leading-9 text-ink',
  h2: 'text-xl font-semibold leading-8 text-ink',
  h3: 'text-base font-semibold leading-7 text-ink',
  quote: 'text-sm leading-7 italic text-slate-300 border-l-2 border-surface-3 pl-3',
  code: 'font-mono text-xs leading-6 text-slate-200 bg-surface-2 rounded-md px-3 py-2',
  bullet: 'text-sm leading-7 text-ink',
  number: 'text-sm leading-7 text-ink',
  todo: 'text-sm leading-7 text-ink',
  divider: '',
};

const PLACEHOLDER: Partial<Record<DocBlockStyle, string>> = {
  h1: 'Heading',
  h2: 'Heading',
  h3: 'Heading',
  quote: 'Quote',
  code: 'Code',
};

export function DocEditor({
  docId,
  blocks: initial,
  canEdit,
  onBlocksChanged,
  className,
}: DocEditorProps) {
  const { socket } = useSocket();
  const me = useAuth((state) => state.user);

  const [blocks, setBlocks] = useState<Map<string, DocBlock>>(new Map());
  const [peers, setPeers] = useState<Map<string, DocPresence>>(new Map());
  const [focused, setFocused] = useState<string | null>(null);

  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;
  /** Paragraphs to put the caret in once React has drawn them. */
  const pendingFocus = useRef<{ id: string; at: 'start' | 'end' } | null>(null);
  const nodes = useRef(new Map<string, HTMLTextAreaElement>());

  useEffect(() => {
    setBlocks(new Map(initial.map((block) => [block.id, block])));
  }, [initial]);

  const ordered = useMemo(() => sortBlocks([...blocks.values()]), [blocks]);

  const apply = useCallback(
    (mutate: (next: Map<string, DocBlock>) => void) => {
      setBlocks((current) => {
        const next = new Map(current);
        mutate(next);
        onBlocksChanged?.(sortBlocks([...next.values()]));
        return next;
      });
    },
    [onBlocksChanged],
  );

  /* ------------------------------- realtime ------------------------------ */

  useEffect(() => {
    if (!socket) return;

    const onBlock = (p: { docId: string; block: DocBlock }) => {
      if (p.docId !== docId) return;
      apply((next) => next.set(p.block.id, p.block));
    };
    const onRemove = (p: { docId: string; blockIds: string[] }) => {
      if (p.docId !== docId) return;
      apply((next) => {
        for (const id of p.blockIds) next.delete(id);
      });
    };
    const onCursor = (p: DocPresence & { docId: string }) => {
      if (p.docId !== docId) return;
      setPeers((current) => new Map(current).set(p.userId, p));
    };
    const onLeft = (p: { docId: string; userId: string }) => {
      if (p.docId !== docId) return;
      setPeers((current) => {
        const next = new Map(current);
        next.delete(p.userId);
        return next;
      });
    };

    socket.on('doc:block', onBlock);
    socket.on('doc:remove', onRemove);
    socket.on('doc:cursor', onCursor);
    socket.on('doc:left', onLeft);
    return () => {
      socket.off('doc:block', onBlock);
      socket.off('doc:remove', onRemove);
      socket.off('doc:cursor', onCursor);
      socket.off('doc:left', onLeft);
    };
  }, [socket, docId, apply]);

  /* Say where the caret is, but not on every keystroke. */
  useEffect(() => {
    if (!socket || !focused) return;
    const timer = setTimeout(() => {
      socket.emit('doc:cursor', { docId, blockId: focused });
    }, 150);
    return () => clearTimeout(timer);
  }, [socket, docId, focused]);

  /* Put the caret in a paragraph the moment it exists. */
  useEffect(() => {
    const pending = pendingFocus.current;
    if (!pending) return;
    const node = nodes.current.get(pending.id);
    if (!node) return;
    pendingFocus.current = null;
    node.focus();
    const at = pending.at === 'start' ? 0 : node.value.length;
    node.setSelectionRange(at, at);
  }, [ordered]);

  /* ------------------------------- editing ------------------------------- */

  const send = useCallback(
    (block: DocBlock) => {
      apply((next) => next.set(block.id, block));
      socket?.emit('doc:block', { docId, block });
    },
    [apply, docId, socket],
  );

  const remove = useCallback(
    (ids: string[]) => {
      apply((next) => {
        for (const id of ids) next.delete(id);
      });
      socket?.emit('doc:remove', { docId, blockIds: ids });
    },
    [apply, docId, socket],
  );

  const stamp = useCallback(
    (block: DocBlock, changes: Partial<DocBlock>): DocBlock => ({
      ...block,
      ...changes,
      updatedBy: me?.id ?? '',
      updatedAt: Date.now(),
    }),
    [me?.id],
  );

  /**
   * Adds a paragraph after `afterId`.
   *
   * The new one takes an order between its neighbours rather than an index,
   * so nothing below it moves — and nothing below it has to be broadcast as
   * an edit that could collide with someone typing there.
   */
  const insertAfter = useCallback(
    (afterId: string, text = '', style: DocBlockStyle = 'p') => {
      const list = sortBlocks([...blocksRef.current.values()]);
      const index = list.findIndex((block) => block.id === afterId);
      const before = list[index];
      const after = list[index + 1];

      // Repeated inserts in one spot eventually halve the gap to nothing; when
      // that happens the document is spaced out again rather than losing order.
      if (before && after && needsRenumber(before.order, after.order)) {
        const spaced = renumber(list);
        apply((next) => {
          for (const block of spaced) next.set(block.id, block);
        });
        for (const block of spaced) socket?.emit('doc:block', { docId, block });
        return;
      }

      const created: DocBlock = {
        id: newId(),
        text,
        style,
        order: orderBetween(before?.order, after?.order),
        updatedBy: me?.id ?? '',
        updatedAt: Date.now(),
      };
      pendingFocus.current = { id: created.id, at: 'start' };
      send(created);
    },
    [apply, docId, me?.id, send, socket],
  );

  const onChange = (block: DocBlock, text: string) => {
    // A markdown prefix typed at the start of a paragraph converts it, the way
    // every editor of this shape behaves: "## " becomes a heading.
    if (block.style === 'p' && /^(#{1,3}|[-*]|\d+[.)]|>|---)\s?$/.test(text)) {
      const parsed = parseMarkdownLine(`${text}x`);
      if (parsed.style !== 'p') {
        send(stamp(block, { style: parsed.style, text: '' }));
        return;
      }
    }
    send(stamp(block, { text: text.slice(0, DOC_MAX_BLOCK_LENGTH) }));
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLTextAreaElement>, block: DocBlock) => {
    const node = event.currentTarget;

    if (event.key === 'Enter' && !event.shiftKey && block.style !== 'code') {
      event.preventDefault();
      // Splitting mid-paragraph carries the tail into the new one, so Enter in
      // the middle of a sentence does what it does everywhere else.
      const tail = node.value.slice(node.selectionStart);
      if (tail) send(stamp(block, { text: node.value.slice(0, node.selectionStart) }));
      // A list carries on as a list; everything else drops back to body text.
      const carried: DocBlockStyle =
        block.style === 'bullet' || block.style === 'number' || block.style === 'todo'
          ? block.style
          : 'p';
      insertAfter(block.id, tail, carried);
      return;
    }

    if (event.key === 'Backspace' && node.selectionStart === 0 && node.selectionEnd === 0) {
      // Backspace at the very start un-styles a styled paragraph before it
      // deletes anything — one press to undo a heading, not one to lose it.
      if (block.style !== 'p') {
        event.preventDefault();
        send(stamp(block, { style: 'p' }));
        return;
      }

      const list = sortBlocks([...blocksRef.current.values()]);
      if (list.length <= 1) return;
      const index = list.findIndex((item) => item.id === block.id);
      const previous = list[index - 1];
      if (!previous) return;

      event.preventDefault();
      pendingFocus.current = { id: previous.id, at: 'end' };
      // The text joins the paragraph above rather than vanishing with it.
      if (node.value) send(stamp(previous, { text: previous.text + node.value }));
      remove([block.id]);
      return;
    }

    if (event.key === 'ArrowUp' && node.selectionStart === 0) {
      const list = sortBlocks([...blocksRef.current.values()]);
      const previous = list[list.findIndex((item) => item.id === block.id) - 1];
      if (previous) {
        event.preventDefault();
        pendingFocus.current = { id: previous.id, at: 'end' };
        setBlocks((current) => new Map(current));
      }
      return;
    }

    if (event.key === 'ArrowDown' && node.selectionStart === node.value.length) {
      const list = sortBlocks([...blocksRef.current.values()]);
      const next = list[list.findIndex((item) => item.id === block.id) + 1];
      if (next) {
        event.preventDefault();
        pendingFocus.current = { id: next.id, at: 'start' };
        setBlocks((current) => new Map(current));
      }
    }
  };

  /* ------------------------------ toolbar -------------------------------- */

  /**
   * The paragraph the toolbar acts on.
   *
   * The last one focused rather than the one focused right now: pressing a
   * toolbar button takes focus out of the textarea, and a toolbar that lost
   * its target the moment you reached for it would be unusable.
   */
  const target = focused ? blocks.get(focused) : undefined;

  const setStyle = useCallback(
    (style: DocBlockStyle) => {
      if (!target || !canEdit) return;
      /* Pressing the style a paragraph already has turns it back into body
         text, so one button both applies and removes — the same behaviour as
         backspacing at the start of a styled line. */
      const next = target.style === style ? 'p' : style;
      send(stamp(target, { style: next, ...(next === 'todo' ? {} : { checked: undefined }) }));
      nodes.current.get(target.id)?.focus();
    },
    [canEdit, send, stamp, target],
  );

  /**
   * Marks the selection in the focused paragraph.
   *
   * The textarea is read for the selection rather than React state, because
   * the selection lives in the DOM node and nowhere else; the result is sent
   * as an ordinary block edit, so a collaborator sees emphasis appear the
   * same way they see typing appear.
   */
  const mark = useCallback(
    (kind: DocInlineMark) => {
      if (!target || !canEdit) return;
      const node = nodes.current.get(target.id);
      if (!node) return;

      const result = toggleMark(node.value, node.selectionStart, node.selectionEnd, kind);
      if (result.text.length > DOC_MAX_BLOCK_LENGTH) return;
      send(stamp(target, { text: result.text }));

      /* Restored after React has drawn the new value, or the browser puts the
         caret back at the end and the next keystroke lands outside the mark
         that was just applied. */
      requestAnimationFrame(() => {
        node.focus();
        node.setSelectionRange(result.selectionStart, result.selectionEnd);
      });
    },
    [canEdit, send, stamp, target],
  );

  /**
   * Turns the selection into a link to `href`.
   *
   * The selection is read off the blurred textarea, which keeps its range —
   * the address was typed in the toolbar, so focus is no longer in the
   * paragraph by the time this runs.
   */
  const link = useCallback(
    (href: string) => {
      if (!target || !canEdit) return;
      const node = nodes.current.get(target.id);
      if (!node) return;

      const result = insertLink(node.value, node.selectionStart, node.selectionEnd, href);
      if (result.text.length > DOC_MAX_BLOCK_LENGTH) return;
      send(stamp(target, { text: result.text }));
      requestAnimationFrame(() => {
        node.focus();
        node.setSelectionRange(result.selectionStart, result.selectionEnd);
      });
    },
    [canEdit, send, stamp, target],
  );

  /**
   * Adds a divider under the caret, and an empty paragraph under that.
   *
   * The paragraph matters: a divider is not editable, so a document that ends
   * in one leaves nowhere to carry on typing.
   */
  const divider = useCallback(() => {
    if (!canEdit) return;
    const list = sortBlocks([...blocksRef.current.values()]);
    const index = target ? list.findIndex((block) => block.id === target.id) : list.length - 1;
    const before = list[index];
    const after = list[index + 1];
    if (!before) return;

    /* Both orders are worked out here rather than by calling the inserter
       twice: two inserts in a row both read the paragraphs as they were
       before either landed, and would take the same order as each other. */
    const rule = orderBetween(before.order, after?.order);
    const now = Date.now();
    const author = me?.id ?? '';
    const paragraph: DocBlock = {
      id: newId(),
      text: '',
      style: 'p',
      order: orderBetween(rule, after?.order),
      updatedBy: author,
      updatedAt: now,
    };

    send({
      id: newId(),
      text: '',
      style: 'divider',
      order: rule,
      updatedBy: author,
      updatedAt: now,
    });
    pendingFocus.current = { id: paragraph.id, at: 'start' };
    send(paragraph);
  }, [canEdit, me?.id, send, target]);

  const peersByBlock = useMemo(() => {
    const map = new Map<string, DocPresence[]>();
    for (const peer of peers.values()) {
      if (peer.userId === me?.id) continue;
      map.set(peer.blockId, [...(map.get(peer.blockId) ?? []), peer]);
    }
    return map;
  }, [peers, me?.id]);

  return (
    <div className={cn('flex min-h-0 flex-col', className)}>
      {canEdit && (
        <DocToolbar
          style={target?.style ?? null}
          disabled={!target}
          onStyle={setStyle}
          onMark={mark}
          onDivider={divider}
          onLink={link}
        />
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex max-w-3xl flex-col gap-1 px-4 py-6 sm:px-8">
          {ordered.map((block, index) => {
            const visitors = peersByBlock.get(block.id);

            if (block.style === 'divider') {
              return (
                <div key={block.id} className="group relative py-3">
                  <hr className="border-surface-3" />
                  {canEdit && (
                    <button
                      type="button"
                      aria-label="Remove divider"
                      onClick={() => remove([block.id])}
                      className="absolute right-0 top-1 hidden text-[11px] text-slate-500 hover:text-rose-400 group-hover:block"
                    >
                      Remove
                    </button>
                  )}
                </div>
              );
            }

            // Counts the unbroken run of numbered paragraphs directly above, so a
            // list interrupted by a heading starts again at 1 rather than
            // carrying on from the previous list.
            let listNumber = 0;
            if (block.style === 'number') {
              listNumber = 1;
              for (let i = index - 1; i >= 0 && ordered[i].style === 'number'; i -= 1) {
                listNumber += 1;
              }
            }

            return (
              <div key={block.id} className="relative flex gap-2">
                {block.style === 'bullet' && (
                  <span
                    aria-hidden
                    className="select-none pt-1 text-sm leading-7 text-slate-500"
                  >
                    •
                  </span>
                )}
                {block.style === 'number' && (
                  <span
                    aria-hidden
                    className="select-none pt-1 text-sm leading-7 text-slate-500"
                  >
                    {listNumber}.
                  </span>
                )}
                {block.style === 'todo' && (
                  <input
                    type="checkbox"
                    checked={Boolean(block.checked)}
                    disabled={!canEdit}
                    onChange={(event) => send(stamp(block, { checked: event.target.checked }))}
                    aria-label={block.text || 'To do'}
                    className="mt-2 h-3.5 w-3.5 shrink-0 accent-brand-500"
                  />
                )}

                <div className="relative min-w-0 flex-1">
                  <AutoTextarea
                    ref={(node) => {
                      if (node) nodes.current.set(block.id, node);
                      else nodes.current.delete(block.id);
                    }}
                    value={block.text}
                    readOnly={!canEdit}
                    placeholder={index === 0 ? 'Start writing…' : PLACEHOLDER[block.style]}
                    onChange={(text) => onChange(block, text)}
                    onKeyDown={(event) => onKeyDown(event, block)}
                    onFocus={() => setFocused(block.id)}
                    className={cn(
                      STYLE_CLASS[block.style],
                      block.style === 'todo' && block.checked && 'text-slate-500 line-through',
                    )}
                  />

                  {visitors?.length ? (
                    <span
                      className="pointer-events-none absolute -left-2 top-1 bottom-1 w-0.5 rounded-full"
                      style={{ background: visitors[0].color }}
                      title={visitors.map((peer) => peer.name).join(', ')}
                    />
                  ) : null}
                </div>
              </div>
            );
          })}

          {canEdit && (
            <button
              type="button"
              onClick={() => insertAfter(ordered[ordered.length - 1]?.id ?? '')}
              className="mt-2 self-start rounded-md px-1 py-2 text-left text-sm text-slate-600 transition-colors hover:text-slate-400"
            >
              Click to keep writing…
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * A textarea that grows with its content.
 *
 * Paragraphs are textareas rather than one contenteditable surface because
 * that is what makes each one separately addressable — and it keeps the
 * browser's own undo, spellcheck and IME behaviour working per paragraph
 * instead of having to be reimplemented.
 */
interface AutoTextareaProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  readOnly?: boolean;
  placeholder?: string;
  onKeyDown?: (event: ReactKeyboardEvent<HTMLTextAreaElement>) => void;
  onFocus?: () => void;
}

const AutoTextarea = forwardRef<HTMLTextAreaElement, AutoTextareaProps>(function AutoTextarea(
  { value, onChange, className, ...rest },
  forwarded,
) {
  const inner = useRef<HTMLTextAreaElement | null>(null);

  const resize = useCallback(() => {
    const node = inner.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${node.scrollHeight}px`;
  }, []);

  useEffect(resize, [value, resize]);

  return (
    <textarea
      ref={(node) => {
        inner.current = node;
        if (typeof forwarded === 'function') forwarded(node);
        else if (forwarded) forwarded.current = node;
      }}
      rows={1}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={cn(
        'w-full resize-none overflow-hidden bg-transparent outline-none placeholder:text-slate-600',
        className,
      )}
      {...rest}
    />
  );
});

/** Shown while the socket is still handing over the paragraphs. */
export function DocEditorSkeleton() {
  return (
    <div className="grid h-full place-items-center">
      <Spinner />
    </div>
  );
}
